import { ChangeEvent, FC, Ref, useMemo } from 'react';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Input from '../../../components/Input/Input';
import InputLabel from '../../../components/Manager/InputLabel/InputLabel';
import Combobox from '../../../components/Combobox/Combobox';
import TextArea from '../../../components/Manager/GridInput/components/TextArea/TextArea';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { ControlItemValue, StaticControlItem } from '../../../types/types';
import {
  DecimalSeparatorMode,
  DelimiterMode,
  ExportEncoding,
  ExportFormat,
  JsonValueMode,
} from '../types';
import {
  COMMA,
  CSV,
  CUSTOM,
  JSON_FORMAT,
  SEMICOLON,
  TAB,
  TXT,
  XLSX,
  XML,
} from '../constants';
import resolveCustomDelimiterError from '../utils/resolveCustomDelimiterError';

interface ExportOptionsPanelProps {
  isWithSubsetsMode: boolean;
  hasSelectedRows: boolean;
  selectedRowsCount: number;
  exportAll: boolean;
  useAllRows: boolean;
  format: ExportFormat;
  encoding: ExportEncoding;
  delimiterMode: DelimiterMode;
  customDelimiter: string;
  decimalSeparator: DecimalSeparatorMode;
  jsonValueMode: JsonValueMode;
  includeColumnNames: boolean;
  useDisplayFormatting: boolean;
  header: string;
  footer: string;
  isEmptyCustomDelimiterRejected: boolean;
  customDelimiterRef: Ref<HTMLInputElement>;
  onFormatChange: (value: ControlItemValue) => void;
  onEncodingChange: (value: ControlItemValue) => void;
  onDelimiterModeChange: (value: ControlItemValue) => void;
  onDecimalSeparatorChange: (value: ControlItemValue) => void;
  onJsonValueModeChange: (value: ControlItemValue) => void;
  onCustomDelimiterChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onHeaderChange: (value: string) => void;
  onFooterChange: (value: string) => void;
  setExportAll: (value: boolean) => void;
  setIncludeColumnNames: (value: boolean) => void;
  setUseDisplayFormatting: (value: boolean) => void;
}

const ExportOptionsPanel: FC<ExportOptionsPanelProps> = ({
  isWithSubsetsMode,
  hasSelectedRows,
  selectedRowsCount,
  exportAll,
  useAllRows,
  format,
  encoding,
  delimiterMode,
  customDelimiter,
  decimalSeparator,
  jsonValueMode,
  includeColumnNames,
  useDisplayFormatting,
  header,
  footer,
  isEmptyCustomDelimiterRejected,
  customDelimiterRef,
  onFormatChange,
  onEncodingChange,
  onDelimiterModeChange,
  onDecimalSeparatorChange,
  onJsonValueModeChange,
  onCustomDelimiterChange,
  onHeaderChange,
  onFooterChange,
  setExportAll,
  setIncludeColumnNames,
  setUseDisplayFormatting,
}) => {
  const { t } = useTranslate();
  const isJsonFormat = format === JSON_FORMAT;
  const isJsonTypedMode = isJsonFormat && jsonValueMode === 'typed';
  // XLSX is a binary OOXML zip, so the text encoding option does not apply.
  const showEncoding =
    !isJsonFormat &&
    format !== XLSX &&
    (isWithSubsetsMode || format === TXT || format === XML || format === CSV);
  const showDelimitedOptions =
    !isWithSubsetsMode && (format === TXT || format === CSV);
  const showDecimalSeparator = !isJsonTypedMode;
  const showUseDisplayFormatting = !isJsonTypedMode;
  const showIncludeColumnNames = !isJsonFormat;
  const showHeaderFooter = format !== XML && !isJsonFormat;

  const formatOptions = useMemo<StaticControlItem[]>(
    () => [
      { label: t('export.format.xlsx'), value: XLSX },
      { label: t('export.format.csv'), value: CSV },
      { label: t('export.format.txt'), value: TXT },
      { label: t('export.format.xml'), value: XML },
      { label: t('export.format.json'), value: JSON_FORMAT },
    ],
    [t]
  );

  const availableFormatOptions = useMemo<StaticControlItem[]>(
    () =>
      isWithSubsetsMode
        ? formatOptions.filter(
            (option) => option.value === XML || option.value === JSON_FORMAT
          )
        : formatOptions,
    [isWithSubsetsMode, formatOptions]
  );

  const jsonValueModeOptions = useMemo<StaticControlItem[]>(
    () => [
      { label: t('export.jsonValueMode.string'), value: 'string' },
      { label: t('export.jsonValueMode.typed'), value: 'typed' },
    ],
    [t]
  );

  const encodingOptions = useMemo<StaticControlItem[]>(
    () => [
      { label: t('export.encodingUtf8'), value: 'utf-8' },
      { label: t('export.encodingWindows1250'), value: 'windows-1250' },
      { label: t('export.encodingAscii'), value: 'ascii' },
    ],
    [t]
  );

  const delimiterOptions = useMemo<StaticControlItem[]>(
    () => [
      { label: t('export.delimiterSemicolon'), value: SEMICOLON },
      { label: t('export.delimiterComma'), value: COMMA },
      { label: t('export.delimiterTab'), value: TAB },
      { label: t('export.delimiterCustom'), value: CUSTOM },
    ],
    [t]
  );

  const decimalSeparatorOptions = useMemo<StaticControlItem[]>(
    () => [
      { label: t('export.decimalSeparatorDisplay'), value: 'display' },
      { label: t('export.decimalSeparatorComma'), value: 'comma' },
      { label: t('export.decimalSeparatorDot'), value: 'dot' },
    ],
    [t]
  );

  return (
    <section className="dataset-export-options">
      <div className="dataset-export-option-group">
        <h2>{t('export.scope')}</h2>
        {hasSelectedRows && !exportAll && (
          <p className="dataset-export-selected-count">
            {t('export.selectedRowsCount').replace(
              '{count}',
              String(selectedRowsCount)
            )}
          </p>
        )}
        <Checkbox
          value={useAllRows}
          onChange={setExportAll}
          disabled={!hasSelectedRows}
          label={t('export.exportAll')}
        />
        {!hasSelectedRows && (
          <p className="dataset-export-option-note">
            {t('export.noSelectedRowsExportAll')}
          </p>
        )}
      </div>

      <div className="dataset-export-option-group">
        <h2>{t('export.options')}</h2>
        <Combobox
          label={t('export.format')}
          name="export-format"
          value={format}
          onChange={onFormatChange}
          options={availableFormatOptions}
        />
        {isWithSubsetsMode && (
          <p className="dataset-export-option-note">
            {t('export.xmlOnlyForSubsets')}
          </p>
        )}

        {isJsonFormat && (
          <Combobox
            label={t('export.jsonValueMode')}
            name="export-json-value-mode"
            value={jsonValueMode}
            onChange={onJsonValueModeChange}
            options={jsonValueModeOptions}
          />
        )}

        {showEncoding && (
          <Combobox
            label={t('export.encoding')}
            name="export-encoding"
            value={encoding}
            onChange={onEncodingChange}
            options={encodingOptions}
          />
        )}

        {showDelimitedOptions && (
          <>
            <Combobox
              label={t('export.delimiter')}
              name="export-delimiter"
              value={delimiterMode}
              onChange={onDelimiterModeChange}
              options={delimiterOptions}
            />

            {delimiterMode === CUSTOM && (
              <Input
                label={t('export.customDelimiter')}
                type="text"
                value={customDelimiter}
                inputRef={customDelimiterRef}
                error={resolveCustomDelimiterError(
                  customDelimiter,
                  encoding,
                  format,
                  isEmptyCustomDelimiterRejected,
                  t
                )}
                onChange={(_, event) => onCustomDelimiterChange(event)}
              />
            )}
          </>
        )}

        {showDecimalSeparator && (
          <Combobox
            label={t('export.decimalSeparator')}
            name="export-decimal-separator"
            value={decimalSeparator}
            onChange={onDecimalSeparatorChange}
            options={decimalSeparatorOptions}
          />
        )}

        {showIncludeColumnNames && (
          <Checkbox
            value={includeColumnNames}
            onChange={setIncludeColumnNames}
            label={t('export.includeColumnNames')}
          />
        )}
        {showUseDisplayFormatting && (
          <Checkbox
            value={useDisplayFormatting}
            onChange={setUseDisplayFormatting}
            label={
              <InputLabel
                hint={t('export.displayFormattingHint')}
                className="dataset-export-checkbox-label"
              >
                {t('export.useDisplayFormatting')}
              </InputLabel>
            }
          />
        )}
      </div>

      {showHeaderFooter && (
        <div className="dataset-export-option-group">
          <TextArea
            label={t('export.customHeader')}
            value={header}
            onChange={onHeaderChange}
            rows={4}
          />
          <TextArea
            label={t('export.customFooter')}
            value={footer}
            onChange={onFooterChange}
            rows={4}
          />
        </div>
      )}
    </section>
  );
};

export default ExportOptionsPanel;
